<template>
  <dx-modal :value="show" :title="title" size="xl" :closable="!saving"
            @input="close()" id="modal-statement-import">
    <div class="billing-gloss statement-import">
      <div class="bg-stepper" v-if="!readonly">
        <div class="bg-step" v-for="(label, index) in steps" :key="label"
             :class="{ current: stepIndex === index, done: stepIndex > index }">
          <span class="bg-step-n">
            <fa-icon v-if="stepIndex > index" :icon="['fal', 'check']"/>
            <template v-else>{{ index + 1 }}</template>
          </span>
          {{ label }}
        </div>
      </div>

      <template v-if="step === 'file'">
        <div class="stenci-group stenci-alert bg-help">
          <p>
            Envie o XML do demonstrativo de análise de conta ou de pagamento (TISS 3.02 a 4.03).
            Nada é gravado antes da conferência.
          </p>
        </div>
        <div class="upload-area" :class="{ dragging, filled: !!file }">
          <input type="file" class="input-file" accept=".xml,.XML" ref="fileInput"
                 @dragenter="dragging = true" @dragleave="dragging = false"
                 @drop="dragging = false" @change="uploadChange">
          <div class="upload-inner">
            <fa-icon :icon="['fal', file ? 'check-circle' : 'upload']"/>
            <p v-if="file">
              <b>{{ file.name }}</b> · {{ fileSize(file.size) }}
              <button class="btn btn-link btn-sm" @click.prevent="changeFile">Trocar</button>
            </p>
            <p v-else>Arraste o XML do demonstrativo aqui ou clique para escolher</p>
          </div>
        </div>
        <div class="cr-panel" v-if="parseErrors.length > 0">
          <div class="cr-panel-head cr-panel-head-amber">
            <div class="cr-panel-title">
              <fa-icon :icon="['fal', 'exclamation-triangle']" class="cr-panel-icon"/>
              Erros no arquivo
            </div>
            <div class="cr-panel-subtitle">
              Corrija o XML na origem ou peça outro arquivo à operadora. O arquivo atual foi
              mantido.
            </div>
          </div>
          <table class="table table-striped">
            <thead>
            <tr>
              <th>#</th>
              <th>Identificação</th>
              <th>Mensagem de erro</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="error in parseErrors" :key="error.index">
              <td>{{ error.index }}</td>
              <td>{{ error.identification }}</td>
              <td>{{ error.message }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else-if="step === 'review' && preview">
        <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
        <template v-else>
          <div class="upload-area filled mb-2" v-if="file">
            <div class="upload-inner">
              <fa-icon :icon="['fal', 'check-circle']"/>
              <p>
                <b>{{ file.name }}</b> · {{ fileSize(file.size) }}
                <template v-if="preview.statement.version">
                  · TISS {{ preview.statement.version }}
                </template>
                <button class="btn btn-link btn-sm" @click.prevent="backToFile">
                  Trocar arquivo
                </button>
              </p>
            </div>
          </div>

          <div class="bg-note bg-note-amber" v-if="preview.alreadyImported">
            Este demonstrativo (nº {{ preview.statement.number }}) já foi importado em
            {{ preview.alreadyImported.importedAt | date }}.
            <button class="btn btn-link btn-sm" @click="viewExisting">Ver demonstrativo</button>
          </div>

          <div class="cr-box cr-box-grid">
            <div>
              <div class="cr-box-label">Demonstrativo</div>
              <div>
                <b>{{ typeName }} nº {{ preview.statement.number }}</b>
                <template v-if="preview.statement.issueDate">
                  · emitido em {{ preview.statement.issueDate | date }}
                </template>
              </div>
            </div>
            <div>
              <div class="cr-box-label">Operadora</div>
              <div>
                <b>{{ preview.statement.operator.name || 'Operadora' }}</b>
                <template v-if="preview.statement.operator.registroANS">
                  · ANS {{ preview.statement.operator.registroANS }}
                </template>
                <template v-if="preview.statement.operator.cnpj">
                  · CNPJ {{ preview.statement.operator.cnpj | cnpj }}
                </template>
              </div>
            </div>
            <div>
              <div class="cr-box-label">Conteúdo</div>
              <div>{{ contentSummary }}</div>
            </div>
            <div :class="{'has-error': $v.chosenInsuranceId.$error}">
              <div class="cr-box-label">Convênio no Stenci</div>
              <div v-if="preview.insurance && !readonly && !preview.insuranceAmbiguous">
                <fa-icon :icon="['fal', 'check-circle']" class="bg-ic-ok"/>
                {{ preview.insurance.name }}
                <span class="text-xs text-gray">(resolvido pelo registro ANS)</span>
              </div>
              <div v-else-if="readonly">{{ preview.insurance ? preview.insurance.name : '—' }}</div>
              <div v-else>
                <select class="form-select select-sm" v-model="chosenInsuranceId"
                        @change="reviewFile" @blur="$v.chosenInsuranceId.$touch()">
                  <option value="">[Escolha o convênio]</option>
                  <option v-for="option in insuranceOptions" :value="option.id" :key="option.id">
                    {{ option.name }}
                  </option>
                </select>
                <div class="form-input-hint" v-if="$v.chosenInsuranceId.$error">
                  Escolha o convênio
                </div>
                <div class="text-xs text-amber" v-else-if="preview.insuranceAmbiguous">
                  Mais de um convênio usa o registro ANS
                  {{ preview.statement.operator.registroANS }}.
                </div>
                <div class="text-xs text-amber" v-else>
                  Nenhum convênio com o registro ANS {{ preview.statement.operator.registroANS }}.
                </div>
              </div>
            </div>
          </div>

          <div class="cr-summary" v-if="isAnalysis">
            <div class="cr-summary-item">
              <small>Informado</small>
              <div class="cr-summary-value">{{ statementTotals.informed | currency }}</div>
              <span class="cr-summary-hint">{{ informedHint }}</span>
            </div>
            <div class="cr-summary-item">
              <small>Processado</small>
              <div class="cr-summary-value">{{ statementTotals.processed | currency }}</div>
            </div>
            <div class="cr-summary-item loss">
              <small>Glosado</small>
              <div class="cr-summary-value">−{{ statementTotals.glossed | currency }}</div>
              <span class="cr-summary-hint">{{ glossedHint }}</span>
            </div>
            <div class="cr-summary-item highlight">
              <small>Liberado</small>
              <div class="cr-summary-value">{{ statementTotals.released | currency }}</div>
              <span class="cr-summary-hint">título passa a valer isso</span>
            </div>
          </div>
          <div class="cr-summary" v-else>
            <div class="cr-summary-item">
              <small>Bruto pago</small>
              <div class="cr-summary-value">{{ statementTotals.released | currency }}</div>
              <span class="cr-summary-hint">{{ paymentHint }}</span>
            </div>
            <div class="cr-summary-item loss">
              <small>Glosado</small>
              <div class="cr-summary-value">−{{ statementTotals.glossed | currency }}</div>
            </div>
            <div class="cr-summary-item loss">
              <small>Débitos</small>
              <div class="cr-summary-value">−{{ statementTotals.debits | currency }}</div>
              <span class="cr-summary-hint">{{ debitsHint }}</span>
            </div>
            <div class="cr-summary-item done" v-if="statementTotals.credits > 0">
              <small>Créditos</small>
              <div class="cr-summary-value">+{{ statementTotals.credits | currency }}</div>
            </div>
            <div class="cr-summary-item highlight">
              <small>Líquido</small>
              <div class="cr-summary-value">{{ statementTotals.net | currency }}</div>
              <span class="cr-summary-hint">o que entra na conta</span>
            </div>
          </div>

          <div class="bg-match">
            <span class="text-sm text-gray">Conciliação:</span>
            <span class="bg-pill bg-pill-green" :class="{ on: matchFilter === 'all' }"
                  @click="matchFilter = 'all'">
              <fa-icon :icon="['fal', 'check']"/>
                {{ matching.matched }}
                {{ matching.matched === 1 ? 'item casado' : 'itens casados' }}
            </span>
            <span class="bg-pill bg-pill-amber" :class="{ on: matchFilter === 'unmatched' }"
                  @click="matchFilter = matchFilter === 'unmatched' ? 'all' : 'unmatched'">
              <fa-icon :icon="['fal', 'exclamation-triangle']"/>
                {{ matching.unresolved }} não
                {{ matching.unresolved === 1 ? 'conciliado' : 'conciliados' }}
            </span>
            <span class="bg-pill bg-pill-red" v-if="isAnalysis" :class="{ on: matchFilter ===
            'diverge' }"
                  @click="matchFilter = matchFilter === 'diverge' ? 'all' : 'diverge'">
              <fa-icon :icon="['fal', 'times']"/>
                {{ matching.divergences }}
                {{ matching.divergences === 1 ? 'divergência' : 'divergências' }}
            </span>
            <span class="grow"></span>
            <span class="text-xs text-gray">
              Casamos por protocolo → lote, nº guia prestador → guia, sequencial do item → item.
            </span>
          </div>
          <div class="bg-note bg-note-amber" v-if="matching.unresolved > 0 && !readonly">
            {{ matching.unresolved }}
            {{ matching.unresolved === 1 ? 'item não conciliado' : 'itens não conciliados' }}.
            Escolha o destino de cada um ou deixe de fora.
          </div>
          <div class="bg-note bg-note-green"
               v-else-if="matching.matched > 0 && !readonly && isAnalysis">
            Tudo conciliado: {{ matching.matched }} {{ matching.matched === 1 ? 'item' : 'itens' }}
            {{ matchedBatchesText }}.
          </div>

          <statement-review
            :preview="preview"
            :readonly="readonly"
            :filter="matchFilter"
            :insurance-id="effectiveInsuranceId"
            @resolve="resolve"
            @ignore="ignore"
            @unignore="unignore"
            @respond="respond"
          />

          <statement-effects v-if="isAnalysis && preview.effects" :effects="preview.effects"/>

          <statement-settle-box
            v-if="!isAnalysis && preview.payments && !readonly"
            v-model="settle"
            :validator="$v.settle"
            :receivables="preview.payments.receivables"
            :payments="matching.payments"
            :bank-accounts="bankAccounts"
            :payment-methods="paymentMethods"
          />
        </template>
      </template>

      <template v-else-if="step === 'done' && result">
        <div class="empty bg-done">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'check-circle']" size="3x" class="bg-empty-icon-done"/>
          </div>
          <p class="empty-title h5">Demonstrativo aplicado</p>
          <p class="empty-subtitle">{{ doneSummary }}</p>
          <div class="bg-done-lines" v-if="result.settled && result.settled.length">
            <div v-for="financial in result.settled" :key="financial.id">
              <fa-icon :icon="['fal', 'check']" class="bg-ic-ok"/>
              Título {{ financial.document }} baixado
              <template v-if="financial.payment && financial.payment.bankAccount">
                em {{ financial.payment.bankAccount.name }}
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <template slot="footer">
      <template v-if="step === 'file'">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1" :class="{ loading: saving }"
                :disabled="!file || saving" @click="reviewFile">
          <fa-icon :icon="['fal', 'search']"/>Conferir
        </button>
        <button class="btn" :disabled="saving" @click="close()">Cancelar</button>
      </template>
      <template v-else-if="step === 'review'">
        <template v-if="!readonly">
          <button class="btn btn-primary btn-icon btn-icon-left mr-1 tooltip tooltip-right"
                  :data-tooltip="applyDisabledReason"
                  :class="{ loading: saving }"
                  :disabled="!!applyDisabledReason || saving || loading"
                  @click="apply">
            <fa-icon :icon="['fal', 'check']"/>{{ applyLabel }}
          </button>
          <button class="btn btn-gray mr-1" :disabled="saving" @click="backToFile">Voltar</button>
          <span class="text-xs text-amber mr-1" v-if="matching.unresolved > 0">
            {{ matching.unresolved }} {{ matching.unresolved === 1 ? 'pendência' : 'pendências' }}
            de conciliação
          </span>
        </template>
        <button class="btn" :disabled="saving" @click="close()">
          {{ readonly ? 'Fechar' : 'Sair' }}
        </button>
      </template>
      <template v-else>
        <button class="btn btn-primary btn-icon btn-icon-left mr-1" v-if="doneBatchId"
                @click="goToBatch">
          <fa-icon :icon="['fal', 'gavel']"/>Ir para as glosas do lote
        </button>
        <button class="btn" @click="close(true)">Fechar</button>
      </template>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { requiredIf } from 'vuelidate/lib/validators';
import { date } from '@/data/validators';
import { getName as statementTypeName } from '@/data/billing-statement-types';
import { getName as debitTypeName } from '@/data/tiss-payment-debit-types';
import StatementReview from '../components/StatementReview.vue';
import StatementEffects from '../components/StatementEffects.vue';
import StatementSettleBox from '../components/StatementSettleBox.vue';
import { fileSize, idOf, num } from '../helpers';

const STEPS = ['Arquivo', 'Conferência', 'Aplicado'];

export default {
  components: {
    StatementReview,
    StatementEffects,
    StatementSettleBox,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    batchId: {
      type: String,
      default: null,
    },
    insuranceId: {
      type: String,
      default: null,
    },
    statementId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      steps: STEPS,
      step: 'file',
      loading: false,
      saving: false,
      readonly: false,
      dragging: false,
      file: null,
      parseErrors: [],
      preview: null,
      resolutions: [],
      chosenInsuranceId: '',
      insuranceOptions: [],
      matchFilter: 'all',
      settle: {
        enabled: false,
        bankAccountId: '',
        settledAt: null,
        methodId: '',
      },
      bankAccounts: [],
      paymentMethods: [],
      result: null,
    };
  },
  computed: {
    stepIndex() {
      return ['file', 'review', 'done'].indexOf(this.step);
    },
    title() {
      if (this.readonly && this.preview) {
        return `Conferência do demonstrativo nº ${this.preview.statement.number}`;
      }
      return 'Importar demonstrativo';
    },
    isAnalysis() {
      return !this.preview || this.preview.statement.type === 'analysis';
    },
    typeName() {
      return this.preview ? statementTypeName(this.preview.statement.type) : '';
    },
    matching() {
      return (this.preview && this.preview.matching) || {
        matched: 0, unresolved: 0, divergences: 0, unmatched: [], protocols: [], payments: [],
      };
    },
    statementTotals() {
      return (this.preview && this.preview.statement.totals) || {};
    },
    effectiveInsuranceId() {
      if (this.chosenInsuranceId) return this.chosenInsuranceId;
      if (this.preview && this.preview.insurance) return idOf(this.preview.insurance);
      return null;
    },
    contentSummary() {
      const counts = (this.preview && this.preview.statement.counts) || {};
      const parts = [];
      if (counts.payments !== undefined) parts.push(`${counts.payments} ${counts.payments === 1 ? 'pagamento' : 'pagamentos'}`);
      if (counts.protocols !== undefined) parts.push(`${counts.protocols} ${counts.protocols === 1 ? 'protocolo' : 'protocolos'}`);
      if (counts.guides !== undefined) parts.push(`${counts.guides} ${counts.guides === 1 ? 'guia' : 'guias'}`);
      if (counts.items !== undefined) parts.push(`${counts.items} ${counts.items === 1 ? 'item' : 'itens'}`);
      if (counts.glosses !== undefined) parts.push(`${counts.glosses} ${counts.glosses === 1 ? 'glosa' : 'glosas'}`);
      if (counts.debits !== undefined) parts.push(`${counts.debits} ${counts.debits === 1 ? 'débito' : 'débitos'}`);
      return parts.join(' · ') || '—';
    },
    informedHint() {
      const batches = (this.matching.protocols || []).filter(protocol => protocol.batch);
      if (batches.length === 0) return 'nenhum lote conciliado';
      const systemTotal = batches.reduce((sum, protocol) => sum
        + num(protocol.batch.totals && protocol.batch.totals.informed), 0);
      return Math.abs(systemTotal - num(this.statementTotals.informed)) < 0.01
        ? 'igual ao faturado no lote'
        : `faturado no lote: ${this.$options.filters.currency(systemTotal)}`;
    },
    glossedHint() {
      const counts = (this.preview && this.preview.statement.counts) || {};
      const informed = num(this.statementTotals.informed);
      const percent = informed > 0
        ? Math.round((num(this.statementTotals.glossed) / informed) * 1000) / 10
        : 0;
      const parts = [];
      if (counts.glosses !== undefined) parts.push(`${counts.glosses} ${counts.glosses === 1 ? 'glosa' : 'glosas'}`);
      if (percent) parts.push(`${String(percent).replace('.', ',')}%`);
      return parts.join(' · ');
    },
    paymentHint() {
      const payments = this.matching.payments || [];
      const dates = payments.map(payment => payment.date).filter(Boolean)
        .map(value => moment(value).format('DD/MM'));
      const protocols = payments.reduce((sum, payment) => sum + payment.protocols.length, 0);
      const parts = [`${protocols} ${protocols === 1 ? 'protocolo' : 'protocolos'}`];
      if (dates.length) parts.push(`${dates.length === 1 ? 'pago em' : 'pagos em'} ${[...new Set(dates)].join(', ')}`);
      return parts.join(' · ');
    },
    debitsHint() {
      const names = new Set();
      (this.matching.payments || []).forEach(payment => (payment.debits || []).forEach((debit) => {
        const name = debitTypeName(debit.type);
        if (name) names.add(name);
      }));
      return [...names].join(' · ');
    },
    matchedBatchesText() {
      const numbers = [...new Set((this.matching.protocols || [])
        .filter(protocol => protocol.batch).map(protocol => protocol.batch.number))];
      if (numbers.length === 0) return '';
      return numbers.length === 1 ? `do lote ${numbers[0]}` : `dos lotes ${numbers.join(', ')}`;
    },
    applyDisabledReason() {
      if (!this.preview) return 'Confira o arquivo';
      if (this.preview.alreadyImported) return 'Demonstrativo já importado';
      if (!this.effectiveInsuranceId) return 'Escolha o convênio';
      if (this.matching.unresolved > 0) return 'Resolva os itens não conciliados';
      return null;
    },
    applyLabel() {
      if (this.matching.unresolved > 0) {
        return `Aplicar (${this.matching.unresolved} ${this.matching.unresolved === 1 ? 'pendência' : 'pendências'})`;
      }
      if (!this.isAnalysis && this.settle.enabled) return 'Aplicar e baixar título';
      return 'Aplicar demonstrativo';
    },
    doneBatchId() {
      const batches = (this.result && this.result.batches) || [];
      if (batches.length === 1) return String(batches[0].id);
      if (this.batchId && batches.some(batch => String(batch.id) === this.batchId)) {
        return this.batchId;
      }
      return null;
    },
    doneSummary() {
      if (!this.result) return '';
      const parts = [];
      const glosses = num(this.result.glosses);
      const batches = this.result.batches || [];
      if (this.result.statement.type === 'analysis') {
        parts.push(`${glosses} ${glosses === 1 ? 'glosa registrada' : 'glosas registradas'}`);
        const effects = this.result.effects || {};
        (effects.receivableChanges || []).forEach((change) => {
          if (!change.paid) parts.push(`título ${change.document || ''} ajustado para ${this.$options.filters.currency(change.to)}`);
        });
      } else {
        const paid = (this.result.payments && this.result.payments.receivables) || [];
        const total = paid.reduce((sum, line) => sum + num(line.paid), 0);
        parts.push(`${this.$options.filters.currency(total)} registrados em ${batches.length} ${batches.length === 1 ? 'lote' : 'lotes'}`);
      }
      return parts.join(' · ');
    },
  },
  validations() {
    return {
      chosenInsuranceId: {
        required: requiredIf(() => this.preview && !this.preview.insurance && !this.readonly),
      },
      settle: {
        bankAccountId: { required: requiredIf(() => this.settle.enabled) },
        settledAt: { required: requiredIf(() => this.settle.enabled), date },
      },
    };
  },
  mounted() {
    if (this.statementId) {
      this.loadExisting(this.statementId);
    }
    if (this.insuranceId) {
      this.chosenInsuranceId = this.insuranceId;
    }
  },
  methods: {
    fileSize,
    uploadChange(e) {
      e.preventDefault();
      const { files } = e.target;
      this.file = files && files.length ? files[0] : null;
      this.parseErrors = [];
    },
    changeFile() {
      this.file = null;
      this.parseErrors = [];
      this.$nextTick(() => {
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      });
    },
    backToFile() {
      this.step = 'file';
      this.preview = null;
      this.resolutions = [];
      this.matchFilter = 'all';
    },
    buildPayload(withSettlement) {
      const formData = new FormData();
      formData.append('file', this.file);
      if (this.effectiveInsuranceId) formData.append('insuranceId', this.effectiveInsuranceId);
      formData.append('resolutions', JSON.stringify(this.resolutions));
      if (withSettlement && this.settle.enabled) {
        formData.append('settleReceivable', 'true');
        formData.append('bankAccountId', this.settle.bankAccountId);
        if (this.settle.methodId) formData.append('methodId', this.settle.methodId);
        if (this.settle.settledAt) formData.append('settledAt', this.settle.settledAt);
      } else {
        formData.append('settleReceivable', 'false');
      }
      return formData;
    },
    reviewFile() {
      if (!this.file) return Promise.resolve();
      const first = this.step === 'file';
      if (first) this.saving = true; else this.loading = true;
      this.parseErrors = [];

      return this.$http.post('/billing-statements/preview', this.buildPayload(false))
        .then(({ data }) => {
          this.preview = data;
          this.insuranceOptions = data.insuranceOptions && data.insuranceOptions.length
            ? data.insuranceOptions
            : this.insuranceOptions;
          if (data.insurance && !this.chosenInsuranceId) {
            this.chosenInsuranceId = idOf(data.insurance);
          }
          if (!data.insurance && this.insuranceOptions.length === 0) this.loadInsurances();
          if (data.statement.type === 'payment') {
            this.prepareSettlement(data);
          }
          this.step = 'review';
        })
        .catch((e) => {
          const body = e && e.response && e.response.data;
          if (body && body.reason === 'statementParse' && Array.isArray(body.errors)) {
            this.parseErrors = body.errors;
            this.step = 'file';
            return;
          }
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
          this.loading = false;
        });
    },
    prepareSettlement(data) {
      const payments = (data.matching && data.matching.payments) || [];
      const firstDate = payments.map(payment => payment.date).find(Boolean);
      if (!this.settle.settledAt && firstDate) {
        this.settle.settledAt = moment(firstDate).format('YYYY-MM-DD');
      }
      if (this.bankAccounts.length === 0) this.loadBankAccounts();
      if (this.paymentMethods.length === 0) this.loadPaymentMethods();
    },
    loadInsurances() {
      return this.$http.get('/insurances', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.insuranceOptions = (data.items || [])
            .map(item => ({ id: item.id, name: item.name }));
        })
        .catch(() => {});
    },
    loadBankAccounts() {
      return this.$http.get('/bank-accounts', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.bankAccounts = data.items || [];
        })
        .catch(() => {});
    },
    loadPaymentMethods() {
      return this.$http.get('/payment-methods', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.paymentMethods = (data.items || []).filter(({ type }) => ['all', 'revenue'].includes(type));
        })
        .catch(() => {});
    },
    resolve(resolution) {
      this.resolutions = [
        ...this.resolutions.filter(entry => entry.key !== resolution.key),
        resolution,
      ];
      return this.reviewFile();
    },
    ignore(key) {
      return this.resolve({ key, ignore: true });
    },
    unignore(key) {
      this.resolutions = this.resolutions.filter(entry => entry.key !== key);
      return this.reviewFile();
    },
    apply() {
      this.$v.$touch();
      if (this.$v.$error || this.applyDisabledReason) return Promise.resolve();

      this.saving = true;
      return this.$http.post('/billing-statements', this.buildPayload(true))
        .then(({ data }) => {
          this.result = data;
          this.step = 'done';
          this.$toast.success(`Demonstrativo nº ${data.statement.number} aplicado.`);
          (data.settled || []).forEach((financial) => {
            const bank = financial.payment && financial.payment.bankAccount
              ? financial.payment.bankAccount.name
              : null;
            this.$toast.success(`Título ${financial.document} baixado${bank ? ` em ${bank}` : ''}.`);
          });
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    loadExisting(statementId) {
      this.readonly = true;
      this.loading = true;
      this.step = 'review';
      return this.$http.get(`/billing-statements/${statementId}`)
        .then(({ data }) => {
          this.preview = this.fromStored(data);
        })
        .catch((e) => {
          this.$toast.error(e);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fromStored(statement) {
      const batches = statement.batches || [];
      const batchOf = id => batches.find(batch => idOf(batch) === String(id)) || null;
      const unmatched = (statement.matching && statement.matching.unmatched) || [];
      const protocols = (statement.protocols || []).map((protocol, index) => ({
        ...protocol,
        key: `p${index}`,
        batch: batchOf(protocol.batchId),
        matchedBy: protocol.batchId ? 'auto' : null,
        guides: (protocol.guides || []).map((guide, guideIndex) => ({
          ...guide,
          key: `p${index}g${guideIndex}`,
          account: guide.accountId
            ? {
              _id: guide.accountId,
              referral: { provider: guide.numeroGuiaPrestador },
              patient: null,
            }
            : null,
          items: (guide.items || []).map((item, itemIndex) => ({
            ...item,
            key: `p${index}g${guideIndex}i${itemIndex}`,
            item: item.itemId ? { _id: item.itemId } : null,
            divergence: null,
          })),
        })),
      }));
      const payments = (statement.payments || []).map((payment, index) => ({
        ...payment,
        protocols: (payment.protocols || []).map((protocol, protocolIndex) => ({
          ...protocol,
          key: `y${index}p${protocolIndex}`,
          batch: batchOf(protocol.batchId),
        })),
      }));
      const matched = protocols.reduce((sum, protocol) => sum
        + protocol.guides.reduce((guideSum, guide) => guideSum
          + guide.items.filter(item => item.item).length, 0), 0);
      return {
        statement: {
          type: statement.type,
          number: statement.number,
          operator: statement.operator || {},
          issueDate: statement.issueDate,
          version: statement.version,
          totals: statement.totals || {},
          counts: {
            protocols: protocols.length,
            guides: protocols.reduce((sum, protocol) => sum + protocol.guides.length, 0),
            items: protocols.reduce((sum, protocol) => sum
              + protocol.guides.reduce((guideSum, guide) => guideSum + guide.items.length, 0), 0),
            glosses: ((statement.applied && statement.applied.glossIds) || []).length,
          },
        },
        alreadyImported: null,
        insurance: statement.insuranceId ? { _id: statement.insuranceId, name: 'Convênio do lote' } : null,
        insuranceOptions: [],
        insuranceAmbiguous: false,
        matching: {
          matched,
          divergences: 0,
          unresolved: 0,
          unmatched,
          protocols,
          payments,
        },
        effects: null,
        payments: null,
      };
    },
    viewExisting() {
      if (!this.preview || !this.preview.alreadyImported) return;
      this.file = null;
      this.loadExisting(String(this.preview.alreadyImported.statementId));
    },
    respond(response) {
      this.$emit('respond', {
        appealId: String(response.appealId),
        prefill: response,
      });
    },
    goToBatch() {
      const id = this.doneBatchId;
      this.close(true);
      if (id && id !== this.batchId) {
        this.$router.push(`/financial/billing-batches/${id}?tab=glosses`);
      }
    },
    close(applied = false) {
      this.$emit('close', applied || this.step === 'done');
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

#modal-statement-import {
  .bg-done {
    padding: $layout-spacing-lg;
    .empty-icon { margin-bottom: $layout-spacing-sm; }
    .bg-done-lines {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin-top: $layout-spacing;
    }
  }
  .statement-import .cr-summary .cr-summary-item { min-width: 6rem; }
}
</style>
